import { useState, useEffect } from "react";
import { Container, Typography, Button } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SummarizeIcon from "@mui/icons-material/Summarize";
import axios from "../../../../axios-order";
import BoxWrapper from "../../../../components/ui/boxWrapper";
import { useLocation } from "react-router-dom";
import { canRead, canWrite } from "../../../../utils/privilegeUtils";

function InwardSummaryDetails() {
  const location = useLocation();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryDetails, setSummaryDetails] = useState(null);
  const [config, setConfig] = useState(null);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const downloadBase64File = (contentType, base64Data, fileName) => {
    const linkSource = `data:${contentType};base64,${base64Data}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  };

  const handleExportExcel = async () => {
    setIsExportingExcel(true);
    if (reportData) {
      try {
        const response = await axios.get(
          `/gstr9_comparison_report/${reportData}`,
        );
        const contentType =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const base64Data = response.data.file;
        if (!base64Data) {
          throw new Error("Base64 data is missing in the response");
        }
        const filename = `${summaryDetails?.comparison_type2 || "InwardComparison"}.xlsx`;
        downloadBase64File(contentType, base64Data, filename);
      } catch (error) {
        console.log(error);
      } finally {
        setIsExportingExcel(false);
      }
    }
  };

  useEffect(() => {
    const item = location.state?.reportId || null;
    setReportData(item);
    console.log("Passed item:", item);
  }, [location]);

  useEffect(() => {
    const getComparisonConfig = (summaryDetails) => {
      const COMPARISON_CONFIGS = {
        "books vs gstr2b": {
          leftLabel: "AS PER BOOKS",
          rightLabel: "AS PER GSTR2B",
          showTaxableValue: true,
          leftMap: {
            taxable_value: "books_taxable_value",
            integrated: "books_integrated_tax",
            central: "books_central_tax",
            state: "books_state_tax",
            cess: "books_cess",
            total: "books_document_value",
          },
          rightMap: {
            taxable_value: "gstr2b_taxable_value",
            integrated: "gstr2b_integrated_tax",
            central: "gstr2b_central_tax",
            state: "gstr2b_state_tax",
            cess: "gstr2b_cess",
            total: "gstr2b_document_value",
          },
        },

        "gstr2b vs gstr3b": {
          leftLabel: "AS PER GSTR2B",
          rightLabel: "AS PER GSTR3B",
          showTaxableValue: false,
          leftMap: {
            integrated: "gstr2b_integrated_tax",
            central: "gstr2b_central_tax",
            state: "gstr2b_state_tax",
            cess: "gstr2b_cess",
            total: "total_tax_value",
          },
          rightMap: {
            integrated: "gstr3b_total_integrated_tax",
            central: "gstr3b_total_central_tax",
            state: "gstr3b_total_state_or_ut_tax",
            cess: "gstr3b_total_cess",
            total: "gstr3b_total_tax_value",
          },
        },

        "books vs gstr3b": {
          leftLabel: "AS PER BOOKS",
          rightLabel: "AS PER GSTR3B",
          showTaxableValue: false,
          leftMap: {
            integrated: "books_integrated_tax",
            central: "books_central_tax",
            state: "books_state_tax",
            cess: "books_cess",
            total: "books_total_tax_amount",
          },
          rightMap: {
            integrated: "gstr3b_total_integrated_tax",
            central: "gstr3b_total_central_tax",
            state: "gstr3b_total_state_or_ut_tax",
            cess: "gstr3b_total_cess",
            total: "gstr3b_total_tax_value",
          },
        },
      };
      const type = summaryDetails?.comparison_type2?.toLowerCase();
      if (!summaryDetails?.comparison_data || !type) return null;

      const configKey = type.startsWith("rcm") ? "books vs gstr3b" : type;

      const config = COMPARISON_CONFIGS[configKey];
      if (!config) return null;

      return {
        ...config,
        getFieldValue: (monthData, side, field) => {
          const map = side === "left" ? config.leftMap : config.rightMap;
          return monthData[map[field]];
        },
      };
    };

    async function getComparisonReport() {
      try {
        if (!reportData) return;

        setLoading(true);

        const response = await axios.get(
          `/gstr9_comparison_report_data/${reportData}`,
        );

        const data = response.data.data;

        setSummaryDetails(data);
        setConfig(getComparisonConfig(data));
      } catch (error) {
        console.error("Error fetching API data:", error);
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    }

    getComparisonReport();
  }, [reportData]);

  const numberFormat = (value) => {
    if (value === null || value === undefined || value === "") {
      return "₹ 0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(value);
  };

  const cellCenter = {
    border: "1px solid #c1c1c1",
    padding: "8px",
    textAlign: "center",
    whiteSpace: "nowrap",
  };

  const cellRight = {
    border: "1px solid #c1c1c1",
    padding: "8px",
    textAlign: "right",
    whiteSpace: "nowrap",
  };

  const handleBack = () => {
    window.history.back();
  };

  const months = summaryDetails?.comparison_data
    ? Object.keys(summaryDetails.comparison_data)
    : [];

  return (
    <Container maxWidth={false}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "2%",
          padding: "0 2%",
        }}
      >
        <button
          className="button-outlined-primary"
          style={{
            marginLeft: "0px",
            width: "auto",
            padding: "6px 12px",
            marginTop: "1px",
            fontSize: "14px",
          }}
          onClick={handleBack}
        >
          BACK
        </button>
        <div
          style={{
            flex: "1",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginLeft: "14%",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", textAlign: "center" }}
          >
            INWARD COMPARISON REPORT{" "}
            <SummarizeIcon style={{ marginBottom: "4px", color: "#043010" }} />
          </Typography>
          {summaryDetails && (
            <Typography
              variant="body2"
              sx={{ textAlign: "center", marginTop: "5px" }}
            >
              {summaryDetails.comparison_type2} |{" "}
              {summaryDetails.financial_year} | {summaryDetails.from_month} -{" "}
              {summaryDetails.to_month}
            </Typography>
          )}
        </div>
        <Button
          onClick={handleExportExcel}
          variant="contained"
          color="primary"
          style={{ marginLeft: '2%' }}
          disabled={isExportingExcel}
          endIcon={isExportingExcel ? (
            <span
              style={{
                width: "12px",
                height: "12px",
                border: "2px solid currentColor",
                borderTopColor: "transparent",
                borderRadius: "50%",
                display: "inline-block",
                animation: "spin 0.7s linear infinite",
                flexShrink: 0,
              }}
            />
          ) : (
            <FileDownloadIcon />
          )}
        >
          {isExportingExcel ? "Exporting..." : "Export"}
        </Button>
      </div>

      <BoxWrapper
        style={{ width: "100%", maxWidth: "100%" }}
        boxTitle={
          summaryDetails?.comparison_type2 || "Inward Comparison Report"
        }
      >
        {loading ? (
          <div
            style={{
              color: "black",
              textAlign: "center",
              fontWeight: "bold",
              padding: "20px",
              fontSize: "16px",
            }}
          >
            Loading… Please wait
          </div>
        ) : error ? (
          <p style={{ color: "red", textAlign: "center" }}>{error}</p>
        ) : !summaryDetails || !summaryDetails.comparison_data || !config ? (
          <div
            style={{
              color: "red",
              textAlign: "center",
              fontSize: "16px",
              padding: "20px",
            }}
          >
            No data found!
          </div>
        ) : (
          <div style={{ maxHeight: "600px", overflowY: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "Arial, sans-serif",
                fontSize: "13px",
                tableLayout: "auto",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#d6e3f3",
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                  }}
                >
                  <th
                    style={{
                      border: "1px solid #3b6ea5",
                      padding: "8px",
                      textAlign: "center",
                      fontWeight: "bold",
                      color: "#002060",
                      whiteSpace: "nowrap",
                      background: "#d6e3f3",
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    Month
                  </th>
                  <th
                    colSpan={config.showTaxableValue ? 6 : 5}
                    style={{
                      border: "1px solid #3b6ea5",
                      padding: "8px",
                      textAlign: "center",
                      fontWeight: "bold",
                      color: "#002060",
                      backgroundColor: "#3065d1",
                      //   color: "white",
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    {config.leftLabel}
                  </th>
                  <th
                    colSpan={config.showTaxableValue ? 6 : 5}
                    style={{
                      border: "1px solid #3b6ea5",
                      padding: "8px",
                      textAlign: "center",
                      fontWeight: "bold",
                      backgroundColor: "#1ed457",
                      color: "white",
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    {config.rightLabel}
                  </th>
                  <th
                    colSpan={config.showTaxableValue ? 6 : 5}
                    style={{
                      border: "1px solid #3b6ea5",
                      padding: "8px",
                      textAlign: "center",
                      fontWeight: "bold",
                      backgroundColor: "#d6712d",
                      color: "white",
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    DIFFERENCE
                  </th>
                </tr>
                <tr
                  style={{
                    backgroundColor: "#e8f0f8",
                    position: "sticky",
                    top: "42px",
                    zIndex: 2,
                  }}
                >
                  <th
                    style={{
                      border: "1px solid #3b6ea5",
                      padding: "8px",
                      textAlign: "center",
                      fontWeight: "bold",
                      color: "#002060",
                      whiteSpace: "nowrap",
                      background: "#e8f0f8",
                    }}
                  >
                    Period
                  </th>
                  {config.showTaxableValue && (
                    <th
                      style={{
                        ...cellCenter,
                        background: "#e8f0f8",
                        fontWeight: "bold",
                      }}
                    >
                      Taxable Value
                    </th>
                  )}
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    Integrated Tax
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    Central Tax
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    State Tax
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    CESS
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    Total Tax
                  </th>
                  {config.showTaxableValue && (
                    <th
                      style={{
                        ...cellCenter,
                        background: "#e8f0f8",
                        fontWeight: "bold",
                      }}
                    >
                      Taxable Value
                    </th>
                  )}
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    Integrated Tax
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    Central Tax
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    State Tax
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    CESS
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    Total Tax
                  </th>
                  {config.showTaxableValue && (
                    <th
                      style={{
                        ...cellCenter,
                        background: "#e8f0f8",
                        fontWeight: "bold",
                      }}
                    >
                      Taxable Value
                    </th>
                  )}
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    Integrated Tax
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    Central Tax
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    State Tax
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    CESS
                  </th>
                  <th
                    style={{
                      ...cellCenter,
                      background: "#e8f0f8",
                      fontWeight: "bold",
                    }}
                  >
                    Total Tax
                  </th>
                </tr>
              </thead>
              <tbody>
                {months.map((month, index) => {
                  const monthData = summaryDetails.comparison_data[month];

                  const getLeftValue = (field) => {
                    if (config.getFieldValue) {
                      return config.getFieldValue(monthData, "left", field);
                    }
                    return monthData[
                      config.leftPrefix + config.fields.left[field]
                    ];
                  };

                  const getRightValue = (field) => {
                    if (config.getFieldValue) {
                      return config.getFieldValue(monthData, "right", field);
                    }
                    return monthData[
                      config.rightPrefix + config.fields.right[field]
                    ];
                  };

                  return (
                    <tr key={index}>
                      <td style={cellCenter}>{month}</td>
                      {/* Left Side Data */}
                      {config.showTaxableValue && (
                        <td style={cellRight}>
                          {numberFormat(getLeftValue("taxable_value"))}
                        </td>
                      )}
                      <td style={cellRight}>
                        {numberFormat(getLeftValue("integrated"))}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(getLeftValue("central"))}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(getLeftValue("state"))}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(getLeftValue("cess"))}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(getLeftValue("total"))}
                      </td>
                      {/* Right Side Data */}
                      {config.showTaxableValue && (
                        <td style={cellRight}>
                          {numberFormat(getRightValue("taxable_value"))}
                        </td>
                      )}
                      <td style={cellRight}>
                        {numberFormat(getRightValue("integrated"))}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(getRightValue("central"))}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(getRightValue("state"))}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(getRightValue("cess"))}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(getRightValue("total"))}
                      </td>
                      {/* Difference Data */}
                      {config.showTaxableValue && (
                        <td style={cellRight}>
                          {numberFormat(monthData.diff_taxable_value)}
                        </td>
                      )}
                      <td style={cellRight}>
                        {numberFormat(monthData.diff_integrated_tax)}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(monthData.diff_central_tax)}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(monthData.diff_state_tax)}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(monthData.diff_cess)}
                      </td>
                      <td style={cellRight}>
                        {numberFormat(
                          monthData.diff_tax_value ||
                          monthData.diff_document_value,
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </BoxWrapper>
    </Container>
  );
}

export default InwardSummaryDetails;
